 
import styled from '@emotion/styled';
import type { ReactNode } from 'react';
import type { AppTheme } from '@theme/theme';

type TextVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'body3' | 'body4' | 'caption';
type TextWeight = 'regular' | 'bold';

interface TextProps {
  id?: string;
  variant?: TextVariant;
  weight?: TextWeight;
  color?: string;
  className?: string;
  children: ReactNode;
}

const resolveColor = (color: string | undefined, theme: AppTheme): string => {
  if (!color) return theme.colors.neutral.white;
  switch (color) {
    case 'primary': return theme.colors.primary[1000];
    case 'secondary': return theme.colors.secondary[1000];
    case 'tertiary': return theme.colors.tertiary[1000];
    case 'white': return theme.colors.neutral.white;
    case 'highEmphasis': return theme.colors.text.highEmphasis;
    case 'mediumEmphasis': return theme.colors.text.mediumEmphasis;
    case 'mediumLowEmphasis': return theme.colors.text.mediumLowEmphasis;
    case 'disabled': return theme.colors.text.disabled;
    default: return color;
  }
};

const StyledText = styled.span<{ variant: TextVariant; weight: TextWeight; color?: string }>`
  font-family: ${(props) => props.theme.typography.fontFamily};
  color: ${(props) => resolveColor(props.color, props.theme)};

  ${(props) => {
    const typography = props.theme.typography;
    // Convert variant_weight to camelCase (e.g., 'body1' + 'bold' -> 'body1Bold')
    const variantKey = props.variant && props.weight 
      ? `${props.variant}${props.weight.charAt(0).toUpperCase() + props.weight.slice(1)}` as keyof typeof typography
      : props.variant as keyof typeof typography;
    const style = typography[variantKey] || typography[props.variant as keyof typeof typography];

    if (!style || typeof style === 'string') {
      return `
        font-size: 16px;
        font-weight: 400;
        line-height: 1.5;
      `;
    }

    return `
      font-size: ${style.fontSize};
      font-weight: ${style.fontWeight};
      line-height: ${style.lineHeight};
      ${style.letterSpacing ? `letter-spacing: ${style.letterSpacing};` : ''}
    `;
  }}
`;

export const Text = ({
  id,
  variant = 'body1',
  weight = 'regular',
  color,
  className,
  children,
}: TextProps) => {
  return (
    <StyledText
      id={id}
      variant={variant}
      weight={weight}
      color={color}
      className={className}
    >
      {children}
    </StyledText>
  );
};
